/**
 * <cart-assembly> — Montageservice (assembly service) cart controller.
 *
 * Wraps the existing event bus (`on:cart:add`, `on:line-item:change`) to keep
 * a hidden Montageservice line in lockstep with the parent furniture line it
 * was added with.
 *
 * Linking model
 * -------------
 *   parent  line item.properties._assembly_id                 = <uuid>
 *           line item.properties._assembly_service_variant_id = <variant id>
 *   service line item.properties._assembly_id                 = <uuid> (matches parent)
 *           line item.properties._assembly_for                = <product title>
 *
 * Each opted-in furniture piece carries its own UUID and the variant id of
 * the Montageservice product attached to it (via the custom.assembly_service
 * metafield), so multiple parents with different service tiers can coexist
 * in the same cart. Shopify keeps lines separate when their properties differ.
 *
 * Reconciliation runs from server state every time a cart event fires (and
 * once on page load). All cart writes are serialised through a Promise chain
 * to avoid race conditions when the user rapid-clicks +/-.
 *
 * Mounted once in layout/theme.liquid:
 *   <cart-assembly hidden></cart-assembly>
 */
if (!customElements.get('cart-assembly')) {
  class CartAssembly extends HTMLElement {
    constructor() {
      super();
      this.queue = Promise.resolve();
      this.retryBudget = 3;
      this.changeHandler = this.scheduleSync.bind(this, {});
      this.toggleHandler = this.handleToggleChange.bind(this);
    }

    connectedCallback() {
      document.addEventListener('change', this.toggleHandler);
      document.addEventListener('on:cart:add', this.changeHandler);
      document.addEventListener('on:line-item:change', this.changeHandler);

      // Cleanup pass: catches orphan service lines from earlier sessions.
      this.scheduleSync({ initial: true });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.toggleHandler);
      document.removeEventListener('on:cart:add', this.changeHandler);
      document.removeEventListener('on:line-item:change', this.changeHandler);
    }

    /**
     * Generates a UUID v4. Falls back to a Math.random based variant for
     * browsers without crypto.randomUUID.
     * @returns {string}
     */
    static uuid() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line no-bitwise
        const r = (Math.random() * 16) | 0;
        // eslint-disable-next-line no-bitwise
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }

    /**
     * Toggles the hidden `properties[_assembly_id]` and
     * `properties[_assembly_service_variant_id]` inputs on the product form
     * whenever the assembly checkbox changes. Disabled inputs are not
     * submitted, so unchecked = no properties added.
     * @param {Event} evt
     */
    handleToggleChange(evt) {
      const checkbox = evt.target;
      if (!checkbox || !checkbox.matches || !checkbox.matches('[data-assembly-toggle-checkbox]')) return;

      const container = checkbox.closest('[data-assembly-toggle]');
      const idInput = container && container.querySelector('[data-assembly-id-input]');
      const variantInput = container && container.querySelector('[data-assembly-service-variant-input]');
      if (!idInput) return;

      if (checkbox.checked) {
        idInput.value = CartAssembly.uuid();
        idInput.disabled = false;
        if (variantInput) variantInput.disabled = false;
      } else {
        idInput.value = '';
        idInput.disabled = true;
        if (variantInput) variantInput.disabled = true;
      }
    }

    /**
     * Queues a reconciliation pass. Multiple events fired in quick succession
     * coalesce because each enqueued sync re-reads server state.
     * @param {object} [opts]
     */
    scheduleSync(opts = {}) {
      this.queue = this.queue.then(() => this.sync(opts)).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[cart-assembly] sync error', err);
      });
    }

    /**
     * Reconciles the cart so every parent with an `_assembly_id` has exactly
     * one matching service line of the same quantity, and orphan service
     * lines are removed.
     * @param {object} [opts]
     */
    async sync() {
      const cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return;

      const groups = CartAssembly.groupByAssemblyId(cart.items);
      const updates = {};
      const adds = [];

      groups.forEach((group, assemblyId) => {
        if (!assemblyId) return;
        const { parent, services } = group;

        if (!parent || parent.quantity === 0) {
          // Orphan service lines: remove them all.
          services.forEach((svc) => {
            updates[svc.key] = 0;
          });
          return;
        }

        if (services.length === 0) {
          // Parent has no service line — probably a fresh add. The service
          // variant to pair with is encoded on the parent line.
          const variantId = parseInt(
            parent.properties && parent.properties._assembly_service_variant_id,
            10
          );
          if (!variantId) {
            // eslint-disable-next-line no-console
            console.warn('[cart-assembly] parent missing _assembly_service_variant_id', parent);
            return;
          }
          adds.push({
            id: variantId,
            quantity: parent.quantity,
            properties: {
              _assembly_id: assemblyId,
              _assembly_for: parent.product_title || ''
            }
          });
          return;
        }

        // Reconcile the first service to parent qty; drop any duplicates.
        const [first, ...rest] = services;
        if (first.quantity !== parent.quantity) {
          updates[first.key] = parent.quantity;
        }
        rest.forEach((dup) => {
          updates[dup.key] = 0;
        });
      });

      const hasAdds = adds.length > 0;
      const hasUpdates = Object.keys(updates).length > 0;
      if (!hasAdds && !hasUpdates) return;

      let mutated = false;

      if (hasAdds) {
        const ok = await this.cartAdd(adds);
        if (ok) mutated = true;
      }

      if (hasUpdates) {
        const ok = await this.cartUpdate(updates);
        if (ok) mutated = true;
      }

      if (mutated) this.refreshCartUI();
    }

    /**
     * @param {Array<object>} items
     * @returns {Promise<boolean>}
     */
    async cartAdd(items) {
      try {
        const body = {
          items,
          sections: this.getCartSections().join(','),
          sections_url: window.location.pathname
        };
        const response = await fetch(this.getRoute('cartAdd'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error(`cart/add ${response.status}`);
        await response.json();
        return true;
      } catch (err) {
        // Service add failed (e.g. service variant OOS). Drop the parent's
        // _assembly_id so we don't keep retrying every event.
        if (this.retryBudget <= 0) return false;
        this.retryBudget -= 1;
        // eslint-disable-next-line no-console
        console.warn('[cart-assembly] cartAdd failed, retries left:', this.retryBudget, err);
        return false;
      }
    }

    /**
     * @param {Object<string, number>} updates - line key → quantity
     * @returns {Promise<boolean>}
     */
    async cartUpdate(updates) {
      try {
        const body = {
          updates,
          sections: this.getCartSections().join(','),
          sections_url: window.location.pathname
        };
        const response = await fetch(this.getRoute('cartUpdate'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error(`cart/update ${response.status}`);
        await response.json();
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[cart-assembly] cartUpdate failed', err);
        return false;
      }
    }

    /**
     * Refreshes cart drawer / cart page so the new lines are reflected.
     */
    refreshCartUI() {
      // Cart drawer listens for this and re-fetches its own sections.
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // On the cart page (no drawer), refresh the <cart-items> directly.
      if (!document.getElementById('cart-drawer')) {
        const cartItems = document.querySelector('cart-items[data-section]');
        if (cartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh();
        }
      }
    }

    /**
     * Reads the current cart state from /cart.js.
     * @returns {Promise<object|null>}
     */
    async fetchCart() {
      try {
        const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
        const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(response.status);
        return await response.json();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[cart-assembly] fetchCart failed', err);
        return null;
      }
    }

    /**
     * Returns the section IDs to refresh on every cart write so the cart
     * icon bubble + cart drawer update without a separate request.
     * @returns {Array<string>}
     */
    getCartSections() {
      const sections = ['cart-icon-bubble'];
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer) {
        const wrapper = cartDrawer.closest('.shopify-section');
        if (wrapper) sections.push(wrapper.id.replace('shopify-section-', ''));
      }
      return sections;
    }

    /**
     * @param {string} name - cartAdd | cartUpdate | cartChange
     * @returns {string}
     */
    getRoute(name) {
      const routes = (window.theme && window.theme.routes) || {};
      const fallback = {
        cartAdd: '/cart/add',
        cartUpdate: '/cart/update',
        cartChange: '/cart/change'
      };
      return routes[name] || fallback[name];
    }

    /**
     * Buckets cart items by `_assembly_id`. Items with no assembly id are
     * ignored. Within a bucket the parent is the first non-service line.
     * @param {Array<object>} items
     * @returns {Map<string, {parent: object|null, services: Array<object>}>}
     */
    static groupByAssemblyId(items) {
      const map = new Map();
      items.forEach((item) => {
        const props = item.properties || {};
        const id = props._assembly_id;
        if (!id) return;
        if (!map.has(id)) map.set(id, { parent: null, services: [] });
        const group = map.get(id);
        if (props._assembly_for) {
          group.services.push(item);
        } else if (!group.parent) {
          group.parent = item;
        }
      });
      return map;
    }
  }

  customElements.define('cart-assembly', CartAssembly);
}
